import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { QuantityPicker } from 'react-qty-picker';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { Button } from '@uncinc/uncinc-react-kitchen-sink';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { formatPrice } from '../../../helpers/commerce';
import { BlockPriceCalculator } from '../../../types/uncinc_profile/blocks';
import InformationOverlay from '../../../components/InformationOverlay';

import './style.scss';

const MIN_NUMBER_OF_PRODUCTS = 1;

const PriceCalculatorBlock = ({ entity, minimal = false }: {
  minimal: boolean, entity: ImmutableEntity<BlockPriceCalculator>
}) => {
  const { t } = useTranslation();
  const calculatorLocationsCount = useSelector<ImmutableRootState<number>, number>((state) => (
    state.getIn(['uiInternal', 'calculatorLocationsCount'], 0)
  ));
  const block = entity.toJS();

  const ctaLink = block.field_link[0];
  const basePrice = block.field_base_price[0];
  const productPrice = block.field_product_price[0];
  const productUnit = block.field_product_unit[0].value;
  const billingPeriod = block.field_billing_period[0].value;
  const ctaMobileText = block.field_cta_mobile_text[0].value;
  const maxProductsAllowed = block.field_max_products_allowed[0].value;
  const productCounterTitle = block.field_product_counter_title[0].value;

  const [count, setCount] = useState(1);
  const [total, setTotal]
    = useState(parseFloat(productPrice.number) + parseFloat(basePrice.number));

  const initialPrice = parseFloat(productPrice.number) + parseFloat(basePrice.number);

  const countChanged = useCallback((value: number) => {
    setCount(value);
    setTotal(value * parseFloat(productPrice.number) + parseFloat(basePrice.number));
  }, [productPrice, basePrice]);

  // Update the count if the locations url param has changed.
  useEffect(() => {
    if (calculatorLocationsCount) {
      countChanged(calculatorLocationsCount);
    }
  }, [calculatorLocationsCount, countChanged]);

  const classes = classNames('Block', 'PriceCalculatorBlock', {
    'PriceCalculatorBlock--minimal': minimal,
  });

  return (
    <div className={classes}>
      {!minimal && (
        <div className="PriceCalculatorBlock__counterBlock PriceCalculatorBlock__blockPadding">
          <h3 className="PriceCalculatorBlock__counterTitle">
            {productCounterTitle}
          </h3>

          <div className="PriceCalculatorBlock__counter">
            <QuantityPicker
              min={MIN_NUMBER_OF_PRODUCTS}
              max={maxProductsAllowed}
              value={count}
              onChange={countChanged}
            />
          </div>
        </div>
      )}
      <div className="PriceCalculatorBlock__totalBlock PriceCalculatorBlock__blockPadding">
        {minimal ? (
          <h4>{t('sso.risk_monitor.calculator_title')}</h4>
        ) : (
          <div className="PriceCalculatorBlock__totalCost">
            <h3 className="PriceCalculatorBlock__totalCostText">&euro;{total <= 0 ? basePrice.formatted : formatPrice(total)}</h3>
            <span className="PriceCalculatorBlock__billingPeriodText">{t('price_calculator_block.billing_period_row', { billingPeriod: t(`price_calculator_block.billing_periods.${billingPeriod}`) })}</span>
          </div>
        )}
        <div className="PriceCalculatorBlock__costBreakdown">
          <div className={`PriceCalculatorBlock__costRows ${count <= 1 ? 'isReversed' : ''}`}>
            <div className="PriceCalculatorBlock__costRow">
              <div>
                {t('price_calculator_block.base_price_row', { productUnit: t(`price_calculator_block.product_units.${productUnit}`, { count: 1 }) })}
              </div>
              <div>
                &euro;{formatPrice(initialPrice)}
              </div>
            </div>
            <div className={`PriceCalculatorBlock__costRow PriceCalculatorBlock__multipleProducts ${count > 1 ? 'isVisible' : ''}`}>
              <div>
                {t(`price_calculator_block.product_units.${productUnit}`, { count: count - 1 })}
              </div>
              <div>
                &euro;{formatPrice(total - initialPrice)}
              </div>
            </div>
          </div>
          {minimal ? (
            <div className="PriceCalculatorBlock__costRow total">
              <div>
                {t('price_calculator_block.billing_period_row', { billingPeriod: t(`price_calculator_block.billing_periods.${billingPeriod}`) })}
                <InformationOverlay
                  overlayKey="price_calculator_billing_info"
                  title={t('price_calculator_block.information_modal.title')}
                  body={t('price_calculator_block.information_modal.body')}
                />
              </div>
              <div>
                &euro;{total <= 0 ? basePrice.formatted : formatPrice(total)}
              </div>
            </div>
          ) : (
            <div className="PriceCalculatorBlock__buttonBlock">
              <Button href={`${ctaLink.alias ?? ctaLink.uri}?locations=${count}`} className="Button--secondary">
                <span className="PriceCalculatorBlock__buttonText">{ctaLink.title}</span>
                <span className="PriceCalculatorBlock__buttonTextMobile">{ctaMobileText}</span>
                <i className="icon icon-arrow-right" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceCalculatorBlock;
